import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import DtoQualityThreshold from 'quality/dto/QualityThreshold/DtoQualityThreshold'
import { isNil } from 'lodash'
import { Grid, Icon, Tooltip } from '@mui/material'
import { calculateIndexThreshold, getBoundedThresholdLabel, getDecreasingThresholdLabel, getIncreasingThresholdLabel, THRESHOLD_COLOR, THRESHOLD_COLOR_BY_SIZE, THRESHOLD_DISPLAY, THRESHOLD_TYPE } from 'utils/QualityThresholdUtils'
import { selectedColor } from 'utils/constants/ColorTheme'
import { MAIN_RADIUS } from 'utils/constants/Theme'

const SliderPanel = ({
    title = '',
    subtitle = '',
    analysis = {},
    threshold,
    withThresholdLevels = false,
    onClick,
    overrideColors,
}) => {
    const formattedThresholds = useMemo(() => {
        if (isNil(threshold)) return []

        if (threshold.thresholdType === THRESHOLD_TYPE.BOUNDED) {
            return [
                { label: getBoundedThresholdLabel(0, threshold), color: THRESHOLD_COLOR.red },
                { label: getBoundedThresholdLabel(1, threshold), color: THRESHOLD_COLOR.blue },
                { label: getBoundedThresholdLabel(2, threshold), color: THRESHOLD_COLOR.red },
            ]
        }

        // js cheat, adding boolean give a number: true = 1 and false = 0
        const nbThreshold = 1 + !isNil(threshold.threshold1) + !isNil(threshold.threshold2) + !isNil(threshold.threshold3) + !isNil(threshold.threshold4)

        if (threshold.thresholdType === THRESHOLD_TYPE.INCREASING) {
            return (overrideColors ?? THRESHOLD_COLOR_BY_SIZE[nbThreshold]).map((color, i) => ({
                color,
                label: getIncreasingThresholdLabel(i, threshold),
            }))
        }

        return (overrideColors ?? THRESHOLD_COLOR_BY_SIZE[nbThreshold]).map((color, i) => ({
            color,
            label: getDecreasingThresholdLabel(i, threshold),
        }))
    }, [overrideColors, threshold])

    const nbColor = formattedThresholds.length

    const index = calculateIndexThreshold(analysis, threshold)

    const listMarkers = formattedThresholds.map((_, i) => (
        <Grid key={i} container size={12/nbColor} justifyContent='center' sx={{ marginTop: '-6px' }}>
            {index === i && (<Icon sx={{ color: '#000' }}>network_wifi</Icon>)}
        </Grid>
    ))

    const listThresholds = formattedThresholds.map(({ color, label }, i) => {
        return (
            <Grid key={i} container size={12/nbColor} justifyContent='center' sx={{ backgroundColor: color, color: '#000', padding: '1px 5px' }}>
                {withThresholdLevels && (
                    <span>
                        {label}
                    </span>
                )}
                {!withThresholdLevels && (
                    <Tooltip title={label}>
                        <div style={{ width: '100%', height: '1.3rem' }}/>
                    </Tooltip>
                )}
            </Grid>
        )
    })

    const styles = !isNil(onClick) && {
        cursor: 'pointer',
        ':hover': {
            background: selectedColor,
            transform: 'scaleX(0.99)',
        },
    }

    return (
        <Grid container onClick={onClick} sx={{ borderRadius: MAIN_RADIUS, ...styles }}>
            <Grid container size={12} justifyContent='space-between' alignItems='center' sx={{ backgroundColor: isNil(threshold) ? '#9e9e9e' : '#000', color: '#FFF', padding: '1px 8px', borderRadius: MAIN_RADIUS }}>
                <Grid container size='grow' justifyContent='flex-start'>
                    <span>
                        {title}
                    </span>
                </Grid>
                <Grid container size='auto' justifyContent='flex-end'>
                    <span>
                        {subtitle}
                    </span>
                </Grid>
            </Grid>
            {threshold?.display === THRESHOLD_DISPLAY.INCREASING ? listMarkers : listMarkers.toReversed()}
            {threshold?.display === THRESHOLD_DISPLAY.INCREASING ? listThresholds : listThresholds.toReversed()}
        </Grid>
    )
}

SliderPanel.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    analysis: PropTypes.shape({
        // DtoAnalysisLight
        // or Analysis like object
    }),
    threshold: PropTypes.oneOfType([
        PropTypes.instanceOf(DtoQualityThreshold),
        PropTypes.shape({
            parameterCode: PropTypes.string,
            threshold1: PropTypes.number,
            threshold2: PropTypes.number,
        }),
    ]),
    withThresholdLevels: PropTypes.bool,
    onClick: PropTypes.func,
    overrideColors: PropTypes.arrayOf(PropTypes.string),
}

export default SliderPanel
